/**
 * React Query hooks for LLM scan data fetching.
 */

import { useQuery } from "@tanstack/react-query";
import {
  getScan,
  getScanResult,
  getScanResults,
  getScanSummary,
  listScans,
} from "@/services/llmScanService";
import type { LLMScanSummary, ResultFilters, ScanFilters } from "@/types/llm";

/**
 * Hook to fetch a paginated list of LLM scans.
 */
export function useLLMScans(filters?: ScanFilters) {
  return useQuery({
    queryKey: ["llm-scans", filters],
    queryFn: () => listScans(filters),
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to fetch a single LLM scan by UUID.
 */
export function useLLMScan(scanUuid: string) {
  return useQuery({
    queryKey: ["llm-scan", scanUuid],
    queryFn: () => getScan(scanUuid),
    enabled: !!scanUuid,
  });
}

/**
 * Hook to fetch aggregated summary statistics for a scan.
 * When autoRefresh is true, polls every 5s while the scan is pending/running
 * and stops automatically once the scan reaches a terminal status.
 */
export function useLLMScanSummary(scanUuid: string, options?: { autoRefresh?: boolean }) {
  return useQuery({
    queryKey: ["llm-scan-summary", scanUuid],
    queryFn: () => getScanSummary(scanUuid),
    enabled: !!scanUuid,
    refetchInterval: options?.autoRefresh
      ? (query) => {
          const data = query.state.data as LLMScanSummary | undefined;
          const status = data?.scan.status;
          return status === "pending" || status === "running" ? 5000 : false;
        }
      : undefined,
  });
}

/**
 * Hook to fetch paginated scan results with optional filters.
 * Supports auto-refresh via refetchInterval (e.g., for in-progress scans).
 */
export function useLLMScanResults(
  scanUuid: string,
  filters?: ResultFilters,
  options?: { refetchInterval?: number | false }
) {
  return useQuery({
    queryKey: ["llm-scan-results", scanUuid, filters],
    queryFn: () => getScanResults(scanUuid, filters),
    enabled: !!scanUuid,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Hook to fetch a single scan result by UUID.
 */
export function useLLMScanResult(resultUuid: string) {
  return useQuery({
    queryKey: ["llm-scan-result", resultUuid],
    queryFn: () => getScanResult(resultUuid),
    enabled: !!resultUuid,
  });
}
